import { type FC, useCallback, useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useApiCall } from "../api/api";
import type { CheckInCode } from "../api/dto/CheckInCode";
import type { ProjectCheckIn } from "../api/dto/ProjectCheckIn";
import { Callout } from "../components/content/Callout";
import { TablePlaceholder } from "../components/data/TablePlaceholder";
import { formatDateTime } from "../util/formatTime";

/**
 * Shows the QR of a check-in. The code in it changes every 30 seconds and is fetched again automatically when it
 * runs out.
 * An expired check-in shows a message instead: its code is no longer meant to be handed out.
 */
export const CheckInCodeModal: FC<{ checkIn: ProjectCheckIn }> = (props) => {
  const { getCheckInCode } = useApiCall();
  const expired = props.checkIn.status === "expired";
  const [code, setCode] = useState<CheckInCode | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      setCode(await getCheckInCode(props.checkIn.uuid));
    } catch {
      setCode(null);
      setFailed(true);
    }
  }, [getCheckInCode, props.checkIn.uuid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per check-in
  useEffect(() => {
    if (!expired) {
      load();
    }
  }, [props.checkIn.uuid]);

  // useApiCall returns new functions on every render, so `load` changes with each render. The timer reads it through
  // a ref: depending on it would restart the timer on every render.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Fetch the next code as soon as this one runs out.
  useEffect(() => {
    if (code === null) {
      return;
    }
    const timer = setTimeout(() => loadRef.current(), code.validForSeconds * 1000);
    return () => clearTimeout(timer);
  }, [code]);

  if (expired) {
    return (
      <div className={"pb-3"}>
        <Callout color={"danger"}>
          Deze toegangscode is verlopen op {formatDateTime(props.checkIn.expiresAt)} en kan niet
          meer worden gebruikt.
        </Callout>
      </div>
    );
  }

  return (
    <div className={"flex flex-col items-center space-y-4 pb-3"}>
      {failed && (
        <TablePlaceholder>
          Code ophalen is mislukt. Is de check-in inmiddels gesloten?
        </TablePlaceholder>
      )}
      {code === null && !failed && <TablePlaceholder>Code laden...</TablePlaceholder>}
      {code !== null && <QRCode value={code.qrPayload} size={320} />}
    </div>
  );
};
